import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import i18next from 'i18next';
import { NOSAMPLE, YES } from '@/helpers/constants';
import useApiRequest from '@/hooks/useApiRequest';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import { setHeaderLanguage } from '@/redux/Header/header.action';
import campaignServices from '@/services/campaignServices';
import { SUMSCriteriaINT } from '@/types';
import { ThemeOptions } from '@/utils/componentStyles';
import Dropdown from '../UI/Dropdown/Dropdown';
import InfoMsg from '../UI/InfoMsg/InfoMsg';
import InputText from '../UI/InputText/InputText';
import Spinner from '../UI/Spinner/Spinner';
import styles from './DialogNewCR.module.scss';

interface DialogNewCRInterface {
  close: () => void;
  setHeaderLang: (lang: string) => void;
}

const MAX_FIN_LENGTH = 17;

const FIN_TYPE = 'FIN';

const ENTRIES = {
  FIN: { EN: 'FIN', DE: 'Fehlerort Recherche über FIN' },
  NOSAMPLE: { EN: 'By Title (Free text, without Quality Live input)', DE: 'Titeleingabe (Freitext, ohne QUALITY LIVE Input)' },
};

const useStyles = makeStyles(({ cartTheme }: ThemeOptions) => ({
  titleRoot: {
    borderBottom: `1px solid ${cartTheme.commonGrey}`,
  },
  buttonsRoot: {
    borderTop: `1px solid ${cartTheme.commonGrey}`,
  },
  dialogPaper: {
    minWidth: '30%',
  },
}));

const DialogNewCR: React.FC<DialogNewCRInterface> = (props) => {
  const { close, setHeaderLang = () => null } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { goTo } = useGoTo();
  const createNewCR = useApiRequest(campaignServices.createNewCampaignRequest);
  const lang = i18next.language.toUpperCase();

  const [state, setState] = useState({
    loading: false,
    bodyShape: '',
    steering: '',
    model: '',
    finNumber: '',
    entries: '',
    partCode: '',
    typeOfErrorCode: '',
    typeOfErrorNumber: '',
    measure: '',
    requestTitle: '',
    selectedPart: '',
    step: 1,
  });

  const [entries, setEntries] = useState(FIN_TYPE);
  const { addToast } = useToastContext();
  const [titleError, setTitleError] = useState([false, false]);
  const [measureError, setMeasureError] = useState(false);

  const handleStateChange = (prop: string, newValue) => {
    if (prop === 'requestTitle') {
      let newTitle = newValue;

      if (newValue.trim().length > 0) {
        newTitle = newValue.replace(/^\s+/g, '');
      }
      const errorMinLength = newTitle.trim().length < 1;
      const errorMaxLength = newTitle.length > 200;
      setTitleError([errorMinLength, errorMaxLength]);

      return setState({ ...state, [prop]: newTitle });
    }

    const newState = {
      ...state,
      [prop]: newValue,
    };

    setState(newState);
  };

  const multipleStateChange = (newProps) => {
    if (newProps.measure) {
      if (newProps.measure.trim().length > 0) {
        newProps.measure = newProps.measure.replace(/^\s+/g, '');
      }
    }
    setMeasureError(newProps?.measure?.length > 38);

    const newState = {
      ...state,
      ...newProps,
    };

    setState(newState);
  };

  const createCampaign = async () => {
    setHeaderLang(lang);
    handleStateChange('loading', true);
    let existsCriteria: undefined | SUMSCriteriaINT;
    const trimmedState = {
      ...state,
      requestTitle: state.requestTitle.trim(),
      measure: state.measure.trim(),
    };

    createNewCR(trimmedState)
      .then((newID) => {
        goTo(`/campaign/${newID}/description${existsCriteria?.sumsRelevant === YES.toUpperCase() ? '?showSUMSPopup=1' : ''}`);
      })
      .catch((err) => {
        handleStateChange('loading', false);
        addToast(err.message, 'ERROR');
      });
  };

  const handleChangeMeasure = (measure: string) => multipleStateChange({ measure });

  const handleChangeEntries = (entrie: string) => {
    setState({
      ...state,
      finNumber: '',
      steering: '',
      bodyShape: '',
      model: '',
      requestTitle: '',
      entries: entrie,
    });
    setEntries(entrie);
  };

  const clearRequestTitleInput = (type: string) => {
    setState({
      ...state,
      loading: true,
      requestTitle: '',
    });

    if (type === FIN_TYPE) {
      setState({ ...state, loading: false, finNumber: type });
    }
  };
  const firstStep = state.step === 1;

  const onClickNextButton = () => {
    if (firstStep) {
      setEntries(FIN_TYPE);
      setState({ ...state, step: 2 });
    } else if (entries === FIN_TYPE) {
      clearRequestTitleInput(FIN_TYPE);
    }
  };

  const onClickBackButton = () => {
    if (state.step === 2) {
      setState({ ...state, step: 1 });
      setEntries(FIN_TYPE);
    } else close();
  };

  return (
    <>
      <Dialog
        open
        id="newCR-dialog"
        classes={{ paper: classes.dialogPaper }}
        maxWidth="md"
        onClose={close}
        aria-labelledby="newCR-dialog-title"
        disableEscapeKeyDown={state.loading}
        data-testid="new-cr-dialog"
      >
        <DialogTitle classes={{ root: classes.titleRoot }} id="newCR-dialog-title">
          {t('dialog.newCampaignRequest')}
        </DialogTitle>
        <DialogContent>
          {state.loading && <Spinner data-testid="new-cr-dialog-loader" isVisible isMask />}
          <div className={styles.Header}>
            <InfoMsg msg={t('campaign.creationLanguageMessage', { lang: t(`languageChip.${lang}`) })} />
          </div>
          <h3>{t(`dialog.${'sampledAffectedVehicle'}`)}</h3>
          <div className={styles.SampleAffectedVehicleContent}>
            {firstStep && (
              <Dropdown
                id="entry-select"
                label={t('dialog.entryAbout')}
                options={ENTRIES}
                value={entries}
                handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleChangeEntries(ev.target.value)}
                formControlClass={styles.Size}
              />
            )}
            {entries === FIN_TYPE && firstStep && (
              <div className={styles.OneColumns}>
                <InputText
                  maxLength={MAX_FIN_LENGTH}
                  id="fin-number"
                  label={t('dialog.finNumber')}
                  handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, finNumber: ev.target.value })}
                  value={state.finNumber}
                  formControlClass={styles.Size}
                />
                <span className={styles.NumberMessage}>{t('dialog.finNumberFormat')}</span>
              </div>
            )}
          </div>
          <div className={styles.SampleAffectedVehicleContent}>
            {(entries === NOSAMPLE || (entries === FIN_TYPE && !firstStep)) && (
              <>
                <InputText
                  id="request-title"
                  label={t('dialog.requestTitle')}
                  handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleStateChange('requestTitle', ev.target.value)}
                  value={state.requestTitle}
                  formControlClass={styles.Size}
                  helperText={[
                    ...((titleError[0]) ? [t('validations.crTitleMinLength')] : []),
                    ...((titleError[1]) ? [t('validations.crTitleMaxLength', { maxLength: 200 })] : []),
                  ]}
                  isMandatory
                />
                <InputText
                  id="measure"
                  label={t('dialog.measure')}
                  handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleChangeMeasure(ev.target.value)}
                  value={state.measure}
                  formControlClass={styles.Size}
                  helperText={[
                    ...(measureError ? [t('validations.crMeasureMaxLength')] : []),
                  ]}
                />
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions classes={{ root: classes.buttonsRoot }}>
          {entries !== NOSAMPLE && firstStep
            ? (
              <Button
                id="next-btn"
                data-testid="next-btn"
                variant="contained"
                color="primary"
                disabled={entries === FIN_TYPE && state.finNumber.length !== MAX_FIN_LENGTH}
                onClick={onClickNextButton}
              >
                {t('dialog.next')}
              </Button>
            )
            : (
              <Button
                id="create-update-btn"
                data-testid="create-update-btn"
                disabled={state.loading || titleError[0] || titleError[1] || !state.requestTitle}
                variant="contained"
                color="primary"
                onClick={createCampaign}
              >
                {t('dialog.create')}
              </Button>
            )}
          <Button
            id="back-cancel-btn"
            data-testid="back-cancel-btn"
            disabled={state.loading}
            color="primary"
            onClick={onClickBackButton}
          >
            {firstStep ? t('dialog.cancel') : t('dialog.back')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const actionCreators = {
  setHeaderLang: setHeaderLanguage,
};

export default connect(null, actionCreators)(DialogNewCR);
